
import { defineComponent } from "@vue/runtime-core";
import { usePublicCourseStore } from "@/stores/publicCourseStore";
import { useMainStore } from "@/stores/mainStore";
import { mapStores } from "pinia";
import {
	EventParticipation,
	EventType,
	getBlankEventTemplate,
	getBlankPractice,
} from "@/models";
import { EventParticipationSearchFilter } from "@/api";
import { setErrorNotification } from "@/utils";
import { LoadAction } from "@ts-pro/vue-eternal-loading";
import Btn from "@/components/ui/Btn.vue";
import SkeletonCard from "@/components/ui/SkeletonCard.vue";
import Spinner from "@/components/ui/Spinner.vue";
import Dialog from "@/components/ui/Dialog.vue";
import VueEternalLoading from "@ts-pro/vue-eternal-loading/src/components/VueEternalLoading/VueEternalLoading.vue";
import PracticeTemplateEditorNew from "@/components/student/PracticeTemplateEditorNew.vue";
import PublicEventParticipationPreview from "@/components/public/PublicEventParticipationPreview.vue";

export default defineComponent({
	name: "PublicFreePractice",
	components: {
		Btn,
		SkeletonCard,
		Spinner,
		Dialog,
		VueEternalLoading,
		PracticeTemplateEditorNew,
		PublicEventParticipationPreview,
	},
	async created() {
		const publicCourseStore = usePublicCourseStore();
		const slug = this.$route.params.slug as string;

		// Ensure course is loaded so we have courseId
		if (!publicCourseStore.courseId) {
			try {
				const courseData = await publicCourseStore.loadPublicCourse(slug);
				publicCourseStore.enrolledCourse = courseData.course;
			} catch (e) {
				setErrorNotification(e);
				this.initialLoading = false;
				return;
			}
		}

		this.initialLoading = false;
		this.firstLoading = true;

		try {
			await this.mainStore.getTags({
				courseId: this.courseId,
				includeExerciseCount: true,
			});
			await this.mainStore.getCourseEventParticipations({
				courseId: this.courseId,
				fromFirstPage: true,
				filter: {
					event_type: EventType.SELF_SERVICE_PRACTICE,
				} as EventParticipationSearchFilter,
			});
		} finally {
			this.firstLoading = false;
		}
	},
	data() {
		return {
			initialLoading: true,
			firstLoading: false,
			isInitialInfiniteLoad: false,
			isEditingRule: false,
			showPracticeDialog: false,
			practiceDialogLoading: false,
			draftEventTemplate: getBlankEventTemplate(),
			loadingParticipations: new Set<string>(),
		};
	},
	computed: {
		...mapStores(useMainStore),
		courseId(): string {
			return usePublicCourseStore().courseId as string;
		},
		slug(): string {
			return this.$route.params.slug as string;
		},
		practiceParticipations(): EventParticipation[] {
			return this.mainStore.practiceParticipations;
		},
		hasTags(): boolean {
			return this.mainStore.tags.length > 0;
		},
	},
	methods: {
		goBack() {
			this.$router.push(`/c/${this.slug}/journey`);
		},
		openNewPracticeDialog() {
			this.draftEventTemplate = getBlankEventTemplate();
			this.showPracticeDialog = true;
		},
		async onBeginPractice() {
			const eventPayload = { ...getBlankPractice(), template: this.draftEventTemplate };
			try {
				this.practiceDialogLoading = true;
				const newPractice = await this.mainStore.createEvent({
					courseId: this.courseId,
					event: eventPayload,
				});
				this.showPracticeDialog = false;
				this.$router.push({
					name: "PublicPracticeParticipationPage",
					params: { slug: this.slug, courseId: this.courseId, examId: newPractice.id },
				});
			} catch (e) {
				setErrorNotification(e);
			} finally {
				this.practiceDialogLoading = false;
			}
		},
		async onLoadMore({ loaded, noMore, error }: LoadAction) {
			try {
				const more = await this.mainStore.getCourseEventParticipations({
					courseId: this.courseId,
					fromFirstPage: false,
					filter: {
						event_type: EventType.SELF_SERVICE_PRACTICE,
					} as EventParticipationSearchFilter,
				});
				more ? loaded() : noMore();
			} catch {
				error();
			}
		},
		async onBookmark(participation: EventParticipation) {
			try {
				this.loadingParticipations.add(participation.id);
				await this.mainStore.partialUpdateEventParticipation({
					courseId: this.courseId,
					eventId: participation.event.id,
					participationId: participation.id,
					changes: { bookmarked: !(participation.bookmarked ?? false) },
				});
			} catch (e) {
				setErrorNotification(e);
			} finally {
				this.loadingParticipations.delete(participation.id);
			}
		},
	},
});
